import useAcvDatadog from '@/composables/useAcvDatadog';
import auth from './../authfile';

/**
 * Composable that handles complete user logout flow including API calls, Auth0 session termination,
 * and local storage cleanup.
 *
 * The logout process:
 * 1. Calls backend API to invalidate server-side session
 * 2. Clears Datadog RUM user context
 * 3. Terminates Auth0 session and redirects to login
 * 4. Removes user data from localStorage
 *
 * Any errors during the logout API call are logged to Datadog but do not prevent
 * the full logout flow from completing.
 *
 * @returns {Object} Logout utilities
 * @returns {Function} logout - Async function that executes the complete logout flow
 *
 * @example
 * // In a component
 * const { logout } = useLogout();
 *
 * const handleLogoutClick = async () => {
 *   await logout();
 *   // User will be redirected to Auth0 login
 * };
 *
 * @example
 * // In a navigation guard or middleware
 * const { logout } = useLogout();
 *
 * if (sessionExpired) {
 *   await logout();
 * }
 */
const useLogout = () => {
  const { addRumError, clearUser } = useAcvDatadog();

  /**
   * Executes the complete user logout flow.
   *
   * Process:
   * 1. Calls `/api/user/logout` to invalidate backend session
   * 2. Logs any API errors to Datadog (but continues logout flow)
   * 3. Clears Datadog RUM user tracking
   * 4. Logs out from Auth0 and redirects to origin URL
   * 5. Removes 'user' and 'user_email' from localStorage
   *
   * @async
   * @returns {Promise<void>} Promise that resolves when Auth0 logout redirect begins
   *
   * @example
   * await logout();
   * // User session terminated and redirected to login page
   *
   * @throws Does not throw - errors are logged to Datadog but logout continues
   *
   * @see {@link https://auth0.github.io/auth0-spa-js/classes/Auth0Client.html#logout Auth0 logout documentation}
   */
  const logout = async () => {
    const { $api } = useNuxtApp();
    // Invalidate server-side session
    let data = null;
    let error = null;
    try {
      data = await $api('/api/user/logout');
    } catch (err) {
      error = err;
    }

    if (error) {
      addRumError('User failed to logout', {
        error: error,
        data: data,
      });
    }

    const config = useRuntimeConfig();
    const auth0 = await auth(
      config.public.AUTH0_DOMAIN,
      config.public.AUTH0_CLIENT_ID,
      config.public.REDIRECT_URL,
    );

    // Clear Datadog user tracking
    clearUser();

    // Terminate Auth0 session and redirect
    await auth0.logout({
      returnTo: window.location.origin,
    });

    // Clean up localStorage to ensure fresh user object on next login
    if (localStorage.getItem('user')) {
      localStorage.removeItem('user');
    }

    if (localStorage.getItem('user_email')) {
      localStorage.removeItem('user_email');
    }
  };

  return {
    logout,
  };
};

export default useLogout;
